<template>
    <div class="observation border border-secondary" :class="isSuccess ? 'is-success' : 'is-error'">
        <div class="input-group mb-3">
            <label class="col-sm-3 col-form-label">Type*</label>
            <div class="col-sm-9">
                <button-radio-container
                        :options="typeOptions"
                        v-model="type"
                />
            </div>
        </div>
        <div class="input-group mb-3">
            <label class="col-sm-3 col-form-label">Interaction Point*</label>
            <div class="col-sm-9">
                <button-radio-container
                        :options="interactionPointOptions"
                        v-model="interactionPoint"
                />
            </div>
        </div>
        <div class="input-group mb-3">
            <label class="col-sm-3 col-form-label">Person*</label>
            <div class="col-sm-9">
                <button-radio-container
                        :options="personOptions"
                        v-model="person"
                />
            </div>
        </div>
        <div v-if="type === 'visualInspection'" class="input-group mb-3">
            <label class="col-sm-3 col-form-label">Inspection Content*</label>
            <div class="col-sm-9">
                <smart-textarea
                        placeholder="Content"
                        v-model="contentInspection"
                        v-on:keydown="onTextAreaKeyDown($event, 'contentInspection')"
                        v-on:eyup="onTextAreaKeyUp($event, 'contentInspection')"
                        maxlength="180"
                />
            </div>
        </div>
        <div v-if="type === 'question'" class="input-group mb-3">
            <label class="col-sm-3 col-form-label">Question*</label>
            <div class="col-sm-9">
                <smart-textarea
                        placeholder="Question"
                        v-model="contentQuestion"
                        v-on:keydown="onTextAreaKeyDown($event, 'contentQuestion')"
                        v-on:keyup="onTextAreaKeyUp($event, 'contentQuestion')"
                        maxlength="180"
                />
            </div>
        </div>
        <div v-if="type === 'question'" class="input-group mb-3">
            <label class="col-sm-3 col-form-label">Answer*</label>
            <div class="col-sm-9">
                <smart-textarea
                        placeholder="Answer"
                        v-model="contentAnswer"
                        v-on:keydown="onTextAreaKeyDown($event, 'contentAnswer')"
                        v-on:keyup="onTextAreaKeyUp($event, 'contentAnswer')"
                        maxlength="180"
                />
            </div>
        </div>
        <div class="input-group mb-3">
            <label class="col-sm-3 col-form-label">Required*</label>
            <div class="col-sm-9">
                <boolean-selector v-model="required" />
            </div>
        </div>
        <div class="input-group mb-3">
            <label class="col-sm-3 col-form-label">Has Dependency*</label>
            <div class="col-sm-9">
                <boolean-selector v-model="hasDependency" />

                <div class="card mt-3" v-if="hasDependency">
                    <div class="card-header">
                        <div class="card-header-title">
                            Dependency observation*
                        </div>
                    </div>
                    <div class="card-body">
                        <span v-if="!displayObservationIds.length">
                            <strong>Cannot add dependency:</strong> No other observations found in observation group.
                        </span>
                        <smart-select
                                v-else
                                class="form-control"
                                v-model="dependencyObservationId"
                                :optionsOrder="displayObservationIds"
                                :options="displayObservations"
                                promptText="Select observation"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="action-container">
            <button class="btn btn-sm btn-secondary btn-move-up ml-2" v-on:click="moveObservationUp" :disabled="!canMoveUp">
                <font-awesome-icon icon="chevron-up" />
            </button>

            <button class="btn btn-sm btn-secondary btn-move-down ml-2" v-on:click="moveObservationDown" :disabled="!canMoveDown">
                <font-awesome-icon icon="chevron-down" />
            </button>

            <button class="btn btn-sm btn-danger btn-delete-observation ml-2" v-on:click="deleteObservation">
                <font-awesome-icon icon="times" />
            </button>
        </div>
    </div>
</template>

<script>
    import ButtonRadioContainer from "@/components/common/ButtonRadioContainer";
    import extend from 'extend';
    import {validateObservation} from "@/utils/admin/indicators/ExceptionValidator";
    import {mapGetters} from 'vuex'
    import * as types from '@/store/mutation-types'
    import BooleanSelector from "@/components/common/BooleanSelector";
    import {getObservationTitle} from "@/utils/admin/titleGenerator";
    import {tryParseInt, smartCompare} from "../../../utils/objectUtils";
    import SmartSelect from "../../common/SmartSelect";
    import SmartTextarea from "../../common/SmartTextarea";

    const typeOptions = {
        'visualInspection': 'Visual Inspection',
        'question': 'Question'
    };

    const interactionPointOptions = {};

    export default {
        name: 'observation',
        components: {
            SmartSelect,
            BooleanSelector,
            ButtonRadioContainer,
            SmartTextarea,
        },
        props: {
            observationGroupId: {
                type: String
            },
            index: {
                type: Number,
            },
            observation: {
                type: Object,
            },
            maxIndex: {
                type: Number,
                default: 0
            }
        },
        data() {
            let data = {
                typeOptions: typeOptions,

                doUpdateValues: true,

                id: null,
                type: '',
                person: '',
                interactionPoint: '',
                contentInspection: '',
                contentQuestion: '',
                contentAnswer: '',

                required: true,
                hasDependency: false,
                dependencyObservationId: null,

                error: null,
            };

            if (this.observation) {
                data = extend(data, this.observation);
            }

            if (!data.dependencyObservationId) {
                data.dependencyObservationId = ''; // Make sure it's an empty string, so the dropdown uses the prompt item
            }

            delete data['index'];

            return data;
        },
        mounted() {
            this.updateValues();
        },
        methods: {
            moveObservationUp() {
                this.$emit('moveUp');
            },
            moveObservationDown() {
                this.$emit('moveDown');
            },
            deleteObservation() {
                this.$emit('delete');
            },
            updateValues() {
                this.$store.commit(types.SET_ACTIVE_INDICATOR_OBSERVATION_DATA, {
                    observationGroupId: this.observationGroupId,
                    observationId: this.id,
                    observationData: {
                        id: this.id,
                        type: this.type,
                        person: this.person,
                        interactionPoint: this.interactionPoint,
                        contentInspection: this.contentInspection,
                        contentQuestion: this.contentQuestion,
                        contentAnswer: this.contentAnswer,
                        required: this.required,
                        hasDependency: this.hasDependency,
                        dependencyObservationId: this.hasDependency ? this.dependencyObservationId : null
                    }
                });
            },
            maybeUpdateValues() {
                if (this.doUpdateValues) {
                    this.updateValues();
                }
            },
            onTextAreaKeyUp(e, field) {
                this[field] = this[field].replace(/[\r\n\v]+/g, '');
            },
            onTextAreaKeyDown(e, field) {
                // Reject ENTER presses
                if (e.which === 13) {
                    e.preventDefault();
                    e.stopPropagation();

                    return false;
                }

                return true;
            },
            validate() {
                try {
                    let result = validateObservation({
                        id: this.id,
                        person: this.person,
                        interactionPoint: this.interactionPoint,
                        type: this.type,
                        contentInspection: this.contentInspection,
                        contentQuestion: this.contentQuestion,
                        contentAnswer: this.contentAnswer,
                        required: this.required,
                        hasDependency: this.hasDependency,
                        dependencyObservationId: this.hasDependency ? this.dependencyObservationId : null
                    });

                    result = typeof(result) !== 'undefined' ? result : true;

                    this.error = null;

                    return result;
                } catch (error) {
                    this.error = error.message || error.toString();

                    return false;
                }
            },
        },
        watch: {
            'observation.dependencyObservationId': function(newValue, oldValue) {
                if (oldValue && !newValue) {
                    this.dependencyObservationId = newValue;
                }
            },
            type() {
                this.maybeUpdateValues();
            },
            person() {
                this.maybeUpdateValues();
            },
            interactionPoint() {
                this.maybeUpdateValues();
            },
            contentInspection() {
                this.maybeUpdateValues();
            },
            contentQuestion() {
                console.log('contentQuestion updated');
                this.maybeUpdateValues();
            },
            contentAnswer() {
                this.maybeUpdateValues();
            },
            required() {
                this.maybeUpdateValues();
            },
            hasDependency() {
                this.maybeUpdateValues();
            },
            dependencyObservationId() {
                this.maybeUpdateValues();
            },
        },
        computed: {
            ...mapGetters({
                activeIndicatorData: 'getActiveIndicatorData',
            }),
            canMoveUp() {
                return this.index > 0;
            },
            canMoveDown() {
                return this.index < this.maxIndex;
            },
            displayObservations() {
                let observations = this.activeIndicatorData.observationGroups[this.observationGroupId].observations;

                let otherObservations = {};

                for (let key in observations) {
                    if (!smartCompare(key, this.id)) {
                        otherObservations[key] = getObservationTitle(observations[key]);
                    }
                }

                return otherObservations;
            },
            displayObservationIds() {
                return Object.keys(this.displayObservations)
            },
            personOptions() {
                if (this.interactionPoint === 'documents') {
                    return {
                        'adult': 'Adult',
                        'child': 'Child',
                        'both': 'Both',
                    };
                }

                return {
                    'adult': 'Adult',
                    'child': 'Child'
                };
            },
            interactionPointOptions() {
                if (this.activeIndicatorData.line === 'first') {
                    return {
                        'person': 'Person',
                        'documents': 'Documents',
                    };
                }

                return {
                    'phone': 'Phone',
                    'luggage': 'Luggage',
                    'person': 'Person',
                    'documents': 'Documents',
                };
            },
            isSuccess() {
                return this.validate();
            },
        }
    }
</script>

<style scoped lang="scss">
    .observation {
        background: #c2cad2;
        padding: 10px;
        position: relative;
        margin-top: 8px;

        &.is-error {
            border: 1px solid #ff7777;
            box-shadow: 2px 2px 2px 0px rgba(0, 0, 0, .5);
            background: #ff9999;
        }
    }

    .col-form-label {
        font-weight: bold;
        text-align: right;
    }

    .action-container {
        position: absolute;
        top: 10px;
        right: 10px;
    }

    .col-sm-9 .button-radio-container {
        margin-left: -15px;
    }
</style>
